import { useMemo, useState } from 'react';
import {
  Plus,
  Search,
  Users,
  UserCheck,
  GitBranch,
  Edit2,
  Trash2,
  AlertCircle,
  CornerDownRight,
  FolderInput,
  ChevronRight,
} from 'lucide-react';
import { Button, Card, Spinner, Badge, Modal, useToast } from '@centraoneplus/ui';
import {
  useTeams,
  useCreateTeam,
  useUpdateTeam,
  useDeleteTeam,
  useAddAgents,
  useRemoveAgent,
  useTeamTicketStats,
} from '../../../hooks/useTeams';
import { TeamTicketCounts } from './TeamTicketCounts';
import { TeamEditor } from './TeamEditor';
import {
  buildTeamTree,
  canAddExistingTeams,
  isParentTeam,
  parentOptionsFor,
  subTeamAgentTotal,
  type TeamViewer,
} from './teamHierarchy';
import { AttachExistingTeamDialog } from './AttachExistingTeamDialog';
import { MoveTeamConfirmation } from './MoveTeamConfirmation';
import { useAuthStore } from '@stores/authStore';
import type { CreateTeamDto, TeamWithMembers, UpdateTeamDto } from '../../../types/team';

function getApiErrorMessage(err: unknown, fallback: string): string {
  const e = err as { response?: { data?: { message?: string } }; message?: string };
  return e?.response?.data?.message || e?.message || fallback;
}

function memberDisplayName(m?: { firstName?: string; lastName?: string; displayName?: string; email?: string }): string {
  if (!m) return '—';
  if (m.displayName) return m.displayName;
  if (m.firstName || m.lastName) return `${m.firstName || ''} ${m.lastName || ''}`.trim();
  return m.email || '—';
}

export function TeamsSettings() {
  const toast = useToast();
  const [search, setSearch] = useState('');
  const [showCreate, setShowCreate] = useState(false);
  // The team a new sub-team is being added to.
  const [subTeamParent, setSubTeamParent] = useState<TeamWithMembers | null>(null);
  const [editing, setEditing] = useState<TeamWithMembers | null>(null);
  // The team an existing team is being added under ("Add existing team").
  const [attachParent, setAttachParent] = useState<TeamWithMembers | null>(null);
  // An edit that changes the team's parent, waiting for the move to be confirmed.
  const [pendingMove, setPendingMove] = useState<{ dto: UpdateTeamDto; agentIds: string[] } | null>(
    null,
  );
  const user = useAuthStore((s) => s.user);
  const viewer: TeamViewer = {
    userId: user?.id,
    isAdmin: user?.role === 'admin' || user?.role === 'owner',
  };
  const [deleting, setDeleting] = useState<TeamWithMembers | null>(null);
  // The team whose details popup is open. Kept as an id so the popup always
  // shows the latest data after an edit/refetch (and closes if it's deleted).
  const [viewingId, setViewingId] = useState<string | null>(null);
  // Blocker message shown INSIDE the delete popup (e.g. team still referenced by
  // a pipeline routing rule) — surfaced in the modal, not a toast.
  const [deleteError, setDeleteError] = useState<string | null>(null);

  const [designationFilter, setDesignationFilter] = useState('');
  const [tagFilter, setTagFilter] = useState('');

  const { data, isLoading, error, refetch } = useTeams({ search: search.trim() || undefined });
  // Ticket counts for every card in one call; cards simply show nothing until it arrives.
  const { data: ticketStats } = useTeamTicketStats();
  const statsByTeam = useMemo(
    () => new Map((ticketStats ?? []).map((s) => [s.teamId, s])),
    [ticketStats],
  );
  const teams = data?.data ?? [];

  // Distinct designations currently in use, so the dropdown only ever offers
  // real tags ("Operations", "Customer Support", ...) instead of a free-text
  // guess that might not match anything.
  const designations = useMemo(
    () =>
      Array.from(new Set(teams.map((t) => t.designation).filter((d): d is string => !!d))).sort(
        (a, b) => a.localeCompare(b),
      ),
    [teams],
  );

  // Same idea for tags, except a team can carry several — flatten first.
  // Doubles as the suggestion list TeamEditor offers when tagging a team.
  const allTags = useMemo(
    () => Array.from(new Set(teams.flatMap((t) => t.tags ?? []))).sort((a, b) => a.localeCompare(b)),
    [teams],
  );

  // Filtered BEFORE the tree is built, same stage `search` already filters
  // at — buildTeamTree already tolerates a sub-team whose parent didn't make
  // the cut (it surfaces as a root instead of vanishing), so this reuses that
  // behaviour rather than inventing a second filtering pass over the tree.
  const filteredTeams = teams
    .filter((t) => !designationFilter || t.designation === designationFilter)
    .filter((t) => !tagFilter || (t.tags ?? []).includes(tagFilter));

  const createMutation = useCreateTeam();
  const updateMutation = useUpdateTeam();
  const deleteMutation = useDeleteTeam();
  const addAgentsMutation = useAddAgents();
  const removeAgentMutation = useRemoveAgent();

  const isSavingCreate = createMutation.isPending;
  const isSavingUpdate =
    updateMutation.isPending || addAgentsMutation.isPending || removeAgentMutation.isPending;
  const isDeleting = deleteMutation.isPending;

  const handleCreate = async (dto: CreateTeamDto) => {
    try {
      await createMutation.mutateAsync(dto);
      setShowCreate(false);
      setSubTeamParent(null);
      toast.success(dto.parentTeamId ? 'Sub-team created' : 'Team created', `${dto.name} is ready.`);
    } catch (err) {
      toast.error('Failed to create team', getApiErrorMessage(err, 'Please try again.'));
    }
  };

  const handleUpdate = async (dto: UpdateTeamDto, agentIds: string[]) => {
    if (!editing) return;
    // Changing the parent is confirmed first, with what the move changes.
    if (dto.parentTeamId !== undefined) {
      setPendingMove({ dto, agentIds });
      return;
    }
    await saveUpdate(dto, agentIds);
  };

  const saveUpdate = async (dto: UpdateTeamDto, agentIds: string[]) => {
    if (!editing) return;
    try {
      // Update core fields first.
      await updateMutation.mutateAsync({ id: editing.id, dto });

      // Diff agents and apply add/remove in parallel.
      const before = new Set(editing.agents.map((a) => a.id));
      const after = new Set(agentIds);
      const toAdd = agentIds.filter((id) => !before.has(id));
      const toRemove = editing.agents.map((a) => a.id).filter((id) => !after.has(id));

      const tasks: Array<Promise<unknown>> = [];
      if (toAdd.length) {
        tasks.push(addAgentsMutation.mutateAsync({ id: editing.id, dto: { agentIds: toAdd } }));
      }
      for (const agentId of toRemove) {
        tasks.push(removeAgentMutation.mutateAsync({ id: editing.id, agentId }));
      }
      await Promise.all(tasks);

      setEditing(null);
      setPendingMove(null);
      toast.success('Team updated', `${dto.name ?? editing.name} saved.`);
    } catch (err) {
      setPendingMove(null);
      toast.error('Failed to update team', getApiErrorMessage(err, 'Please try again.'));
    }
  };

  const handleAttach = async (teamId: string, moveParentAgents: boolean) => {
    if (!attachParent) return;
    const moved = teams.find((t) => t.id === teamId);
    try {
      await updateMutation.mutateAsync({
        id: teamId,
        dto: { parentTeamId: attachParent.id, ...(moveParentAgents ? { moveParentAgents: true } : {}) },
      });
      toast.success('Sub-team added', `${moved?.name ?? 'The team'} is now a sub-team of ${attachParent.name}.`);
      setAttachParent(null);
    } catch (err) {
      toast.error('Could not add the team', getApiErrorMessage(err, 'Please try again.'));
    }
  };

  const handleDelete = async () => {
    if (!deleting) return;
    setDeleteError(null);
    try {
      await deleteMutation.mutateAsync(deleting.id);
      setDeleting(null);
      toast.success('Team deleted', `${deleting.name} was removed.`);
    } catch (err) {
      // Show the blocker inside the popup (keep it open) instead of a toast.
      setDeleteError(
        getApiErrorMessage(
          err,
          'A pipeline routing rule may still reference this team. Reassign it first.',
        ),
      );
    }
  };

  const closeDelete = () => {
    setDeleting(null);
    setDeleteError(null);
  };

  const filteredCount = filteredTeams.length;
  const isFiltered = !!search || !!designationFilter || !!tagFilter;
  // Parents with their sub-teams nested underneath.
  const tree = useMemo(() => buildTeamTree(filteredTeams), [filteredTeams]);
  // Looked up from the full (unfiltered) list — a sub-team's parent shouldn't
  // disappear from its own edit modal just because a designation filter is
  // narrowing the grid.
  const parentOf = (team: TeamWithMembers | null) =>
    team?.parentTeamId ? teams.find((t) => t.id === team.parentTeamId) : undefined;
  const askDelete = (team: TeamWithMembers) => {
    setDeleteError(null);
    setDeleting(team);
  };
  const viewing = viewingId ? (teams.find((t) => t.id === viewingId) ?? null) : null;
  const viewingSubTeams = viewing ? teams.filter((t) => t.parentTeamId === viewing.id) : [];
  const viewingIsParent = !!viewing && (viewingSubTeams.length > 0 || isParentTeam(viewing));
  // Every action in the details popup closes it and opens that action's own dialog.
  const fromView = (action: () => void) => {
    setViewingId(null);
    action();
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
        <div className="flex min-w-0 flex-1 items-center gap-3">
          <Users className="h-6 w-6 flex-shrink-0 text-primary-600" />
          <div className="min-w-0">
            <h2 className="text-xl font-semibold text-gray-900 dark:text-white">Teams</h2>
            <p className="text-sm text-gray-500 dark:text-gray-400">
              Group agents under a supervisor and link to a pipeline for auto-routing. Add
              sub-teams to split a team such as Support into smaller groups.
            </p>
          </div>
        </div>
        <Button onClick={() => setShowCreate(true)} className="flex-shrink-0 whitespace-nowrap">
          <Plus className="mr-2 h-4 w-4" />
          Create Team
        </Button>
      </div>

      {/* Search + designation filter */}
      <div className="flex flex-col gap-3 sm:flex-row sm:items-center">
        <div className="relative max-w-md flex-1">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-400 dark:text-gray-500" />
          <input
            type="text"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder="Search teams by name..."
            className="w-full rounded-lg border border-gray-300 bg-white py-2 pl-9 pr-3 text-sm placeholder-gray-400 focus:border-primary-400 focus:ring-2 focus:ring-primary-500/30 dark:border-gray-600 dark:bg-gray-800 dark:text-gray-100 dark:placeholder-gray-500"
          />
        </div>
        {designations.length > 0 && (
          <select
            value={designationFilter}
            onChange={(e) => setDesignationFilter(e.target.value)}
            aria-label="Filter teams by designation"
            className="rounded-lg border border-gray-300 bg-white py-2 px-3 text-sm focus:border-primary-400 focus:ring-2 focus:ring-primary-500/30 dark:border-gray-600 dark:bg-gray-800 dark:text-gray-100"
          >
            <option value="">All designations</option>
            {designations.map((d) => (
              <option key={d} value={d}>
                {d}
              </option>
            ))}
          </select>
        )}
        {allTags.length > 0 && (
          <select
            value={tagFilter}
            onChange={(e) => setTagFilter(e.target.value)}
            aria-label="Filter teams by tag"
            className="rounded-lg border border-gray-300 bg-white py-2 px-3 text-sm focus:border-primary-400 focus:ring-2 focus:ring-primary-500/30 dark:border-gray-600 dark:bg-gray-800 dark:text-gray-100"
          >
            <option value="">All tags</option>
            {allTags.map((t) => (
              <option key={t} value={t}>
                {t}
              </option>
            ))}
          </select>
        )}
      </div>

      {/* Body */}
      {isLoading ? (
        <div className="flex items-center justify-center py-12">
          <Spinner />
        </div>
      ) : error ? (
        <Card className="p-6">
          <div className="flex items-start gap-3">
            <AlertCircle className="mt-0.5 h-5 w-5 text-red-500" />
            <div className="flex-1">
              <p className="font-medium text-gray-900 dark:text-gray-100">Couldn't load teams</p>
              <p className="text-sm text-gray-500 dark:text-gray-400">
                {getApiErrorMessage(error, 'Please try again.')}
              </p>
            </div>
            <Button variant="secondary" size="sm" onClick={() => refetch()}>
              Retry
            </Button>
          </div>
        </Card>
      ) : filteredCount === 0 ? (
        <Card className="p-12 text-center">
          <Users className="mx-auto mb-3 h-10 w-10 text-gray-300 dark:text-gray-600" />
          <h3 className="mb-1 text-lg font-medium text-gray-900 dark:text-gray-100">
            {isFiltered ? 'No matching teams' : 'No teams yet'}
          </h3>
          <p className="mx-auto mb-5 max-w-md text-sm text-gray-500 dark:text-gray-400">
            {isFiltered
              ? 'Try a different search, designation, or tag.'
              : 'Create your first team to organize agents under a supervisor and route tickets automatically.'}
          </p>
          {!isFiltered && (
            <Button onClick={() => setShowCreate(true)}>
              <Plus className="mr-2 h-4 w-4" />
              Create Team
            </Button>
          )}
        </Card>
      ) : (
        <div className="grid grid-cols-1 gap-4 md:grid-cols-2 xl:grid-cols-3">
          {tree.roots.map((team) => {
            const subTeams = tree.childrenOf[team.id] ?? [];
            // A parent team: it has sub-teams (also true when a search hid them).
            const isParent = subTeams.length > 0 || isParentTeam(team);
            const capacity = team.maxSize;
            const count = team.agentCount ?? team.agents.length;
            const pct = capacity ? Math.min(100, Math.round((count / capacity) * 100)) : null;

            return (
              <Card
                key={team.id}
                padding="none"
                className="flex flex-col transition-shadow hover:shadow-md"
              >
                {/* The whole card opens the details popup; "View details" is the keyboard path. */}
                <div
                  onClick={() => setViewingId(team.id)}
                  className="flex flex-1 cursor-pointer flex-col p-5"
                >
                  <div className="mb-3 flex items-start justify-between gap-3">
                    <div className="min-w-0 flex-1">
                      <h3 className="truncate text-base font-semibold text-gray-900 dark:text-gray-100">
                        {team.name}
                      </h3>
                      {team.parentName && (
                        <p className="truncate text-xs text-gray-500 dark:text-gray-400">
                          Sub-team of {team.parentName}
                        </p>
                      )}
                      {team.designation && (
                        <p className="truncate text-xs text-gray-500 dark:text-gray-400">
                          {team.designation}
                        </p>
                      )}
                    </div>
                    {!team.isActive && <Badge variant="secondary">Inactive</Badge>}
                  </div>

                  {team.tags && team.tags.length > 0 && (
                    <div className="mb-3 flex flex-wrap gap-1.5">
                      {team.tags.map((t) => (
                        <span
                          key={t}
                          className="rounded-full bg-primary-50 px-2 py-0.5 text-xs text-primary-700 dark:bg-primary-900/30 dark:text-primary-300"
                        >
                          {t}
                        </span>
                      ))}
                    </div>
                  )}

                  {team.description && (
                    <p className="mb-4 line-clamp-2 text-sm text-gray-600 dark:text-gray-300">
                      {team.description}
                    </p>
                  )}

                  <div className="mb-4 space-y-2 text-sm">
                    <div className="flex items-center gap-2 text-gray-600 dark:text-gray-300">
                      <UserCheck className="h-4 w-4 flex-shrink-0 text-blue-500" />
                      <span className="truncate">
                        {memberDisplayName(team.supervisor)}{' '}
                        <span className="text-xs text-gray-400">
                          ({isParent ? 'Head' : 'Supervisor'})
                        </span>
                      </span>
                    </div>
                    <div className="flex items-center gap-2 text-gray-600 dark:text-gray-300">
                      <Users className="h-4 w-4 flex-shrink-0 text-green-500" />
                      {isParent ? (
                        <span>
                          {team.subTeams?.length ?? subTeams.length} sub-teams ·{' '}
                          {subTeamAgentTotal(team)} agents
                        </span>
                      ) : (
                        <span>
                          {count}
                          {capacity ? ` / ${capacity}` : ''} agents
                        </span>
                      )}
                    </div>
                    {!isParent && pct !== null && (
                      <div className="h-1.5 overflow-hidden rounded-full bg-gray-100 dark:bg-gray-700">
                        <div
                          className="h-full bg-gradient-to-r from-primary-400 to-primary-600 transition-all"
                          style={{ width: `${pct}%` }}
                        />
                      </div>
                    )}
                    {team.pipelineName && (
                      <div className="flex items-center gap-2 text-gray-600 dark:text-gray-300">
                        <GitBranch className="h-4 w-4 flex-shrink-0 text-purple-500" />
                        <span className="truncate">{team.pipelineName}</span>
                      </div>
                    )}
                  </div>

                  {subTeams.length > 0 && (
                    <ul className="mb-4 space-y-1 border-t border-gray-100 pt-3 dark:border-gray-700">
                      {subTeams.map((sub) => (
                        <li
                          key={sub.id}
                          className="flex items-center gap-2 px-1 py-1 text-sm text-gray-700 dark:text-gray-200"
                        >
                          <CornerDownRight className="h-3.5 w-3.5 flex-shrink-0 text-gray-400" />
                          <span className="min-w-0 flex-1 truncate">
                            {sub.name}
                            {!sub.isActive && (
                              <span className="ml-1 text-xs text-gray-400">(inactive)</span>
                            )}
                          </span>
                          <span className="flex-shrink-0 text-xs text-gray-500 dark:text-gray-400">
                            {sub.agentCount ?? sub.agents.length} agents
                          </span>
                        </li>
                      ))}
                    </ul>
                  )}

                  {/* Pinned to the bottom so the counts and footer line up across a row of cards. */}
                  <div className="mt-auto">
                    <TeamTicketCounts stats={statsByTeam.get(team.id)} isParent={isParent} />
                    <div className="mt-4 flex items-center justify-end border-t border-gray-100 pt-3 dark:border-gray-700">
                      <button
                        type="button"
                        onClick={(e) => {
                          e.stopPropagation();
                          setViewingId(team.id);
                        }}
                        className="inline-flex items-center gap-1 rounded-lg px-2 py-1.5 text-xs font-medium text-primary-600 transition-colors hover:bg-primary-50 dark:text-primary-400 dark:hover:bg-gray-700"
                        aria-label={`View team ${team.name}`}
                      >
                        View details
                        <ChevronRight className="h-3.5 w-3.5" />
                      </button>
                    </div>
                  </div>
                </div>
              </Card>
            );
          })}
        </div>
      )}

      {/* Team details: every card action lives here */}
      <Modal
        isOpen={!!viewing}
        onClose={() => setViewingId(null)}
        title={viewing?.name ?? 'Team'}
        size="lg"
      >
        {viewing && (
          <div className="space-y-5">
            {(viewing.parentName || viewing.designation || !viewing.isActive) && (
              <div className="flex flex-wrap items-center gap-2 text-sm text-gray-500 dark:text-gray-400">
                {viewing.parentName && <span>Sub-team of {viewing.parentName}</span>}
                {viewing.parentName && viewing.designation && <span>·</span>}
                {viewing.designation && <span>{viewing.designation}</span>}
                {!viewing.isActive && <Badge variant="secondary">Inactive</Badge>}
              </div>
            )}

            {viewing.description && (
              <p className="text-sm text-gray-700 dark:text-gray-300">{viewing.description}</p>
            )}

            {viewing.tags && viewing.tags.length > 0 && (
              <div className="flex flex-wrap gap-1.5">
                {viewing.tags.map((t) => (
                  <span
                    key={t}
                    className="rounded-full bg-primary-50 px-2 py-0.5 text-xs text-primary-700 dark:bg-primary-900/30 dark:text-primary-300"
                  >
                    {t}
                  </span>
                ))}
              </div>
            )}

            <dl className="grid grid-cols-1 gap-3 text-sm sm:grid-cols-3">
              <div className="min-w-0">
                <dt className="text-xs text-gray-500 dark:text-gray-400">
                  {viewingIsParent ? 'Head' : 'Supervisor'}
                </dt>
                <dd className="mt-0.5 flex items-center gap-2 text-gray-900 dark:text-gray-100">
                  <UserCheck className="h-4 w-4 flex-shrink-0 text-blue-500" />
                  <span className="truncate">{memberDisplayName(viewing.supervisor)}</span>
                </dd>
              </div>
              <div className="min-w-0">
                <dt className="text-xs text-gray-500 dark:text-gray-400">Agents</dt>
                <dd className="mt-0.5 flex items-center gap-2 text-gray-900 dark:text-gray-100">
                  <Users className="h-4 w-4 flex-shrink-0 text-green-500" />
                  {viewingIsParent ? (
                    <span>
                      {viewing.subTeams?.length ?? viewingSubTeams.length} sub-teams ·{' '}
                      {subTeamAgentTotal(viewing)} agents
                    </span>
                  ) : (
                    <span>
                      {viewing.agentCount ?? viewing.agents.length}
                      {viewing.maxSize ? ` / ${viewing.maxSize}` : ''} agents
                    </span>
                  )}
                </dd>
              </div>
              {viewing.pipelineName && (
                <div className="min-w-0">
                  <dt className="text-xs text-gray-500 dark:text-gray-400">Pipeline</dt>
                  <dd className="mt-0.5 flex items-center gap-2 text-gray-900 dark:text-gray-100">
                    <GitBranch className="h-4 w-4 flex-shrink-0 text-purple-500" />
                    <span className="truncate">{viewing.pipelineName}</span>
                  </dd>
                </div>
              )}
            </dl>

            {viewing.agents.length > 0 && (
              <div>
                <p className="mb-1.5 text-xs text-gray-500 dark:text-gray-400">Members</p>
                <div className="flex flex-wrap gap-1.5">
                  {viewing.agents.map((a) => (
                    <span
                      key={a.id}
                      className="rounded-full bg-gray-100 px-2 py-0.5 text-xs text-gray-700 dark:bg-gray-700 dark:text-gray-200"
                    >
                      {memberDisplayName(a)}
                    </span>
                  ))}
                </div>
              </div>
            )}

            <TeamTicketCounts stats={statsByTeam.get(viewing.id)} isParent={viewingIsParent} />

            {viewingSubTeams.length > 0 && (
              <div>
                <p className="mb-1.5 text-xs text-gray-500 dark:text-gray-400">Sub-teams</p>
                <ul className="space-y-1">
                  {viewingSubTeams.map((sub) => (
                    <li key={sub.id}>
                      <button
                        type="button"
                        onClick={() => setViewingId(sub.id)}
                        className="flex w-full items-center gap-2 rounded-lg px-2 py-1.5 text-left text-sm text-gray-700 transition-colors hover:bg-gray-50 dark:text-gray-200 dark:hover:bg-gray-700"
                        aria-label={`View sub-team ${sub.name}`}
                      >
                        <CornerDownRight className="h-3.5 w-3.5 flex-shrink-0 text-gray-400" />
                        <span className="min-w-0 flex-1 truncate">
                          {sub.name}
                          {!sub.isActive && (
                            <span className="ml-1 text-xs text-gray-400">(inactive)</span>
                          )}
                        </span>
                        <span className="flex-shrink-0 text-xs text-gray-500 dark:text-gray-400">
                          {sub.agentCount ?? sub.agents.length} agents
                        </span>
                        <ChevronRight className="h-3.5 w-3.5 flex-shrink-0 text-gray-400" />
                      </button>
                    </li>
                  ))}
                </ul>
              </div>
            )}

            <div className="flex flex-wrap items-center gap-2 border-t border-gray-100 pt-4 dark:border-gray-700">
              <button
                type="button"
                onClick={() => fromView(() => askDelete(viewing))}
                className="mr-auto inline-flex items-center gap-1.5 whitespace-nowrap rounded-lg px-3 py-2 text-sm font-medium text-red-600 transition-colors hover:bg-red-50 dark:text-red-400 dark:hover:bg-red-900/20"
                aria-label={`Delete ${viewing.parentTeamId ? 'sub-team' : 'team'} ${viewing.name}`}
              >
                <Trash2 className="h-4 w-4" />
                Delete
              </button>
              {/* Two levels only: a sub-team cannot have sub-teams of its own. */}
              {!viewing.parentTeamId && (
                <button
                  type="button"
                  onClick={() => fromView(() => setSubTeamParent(viewing))}
                  className="inline-flex items-center gap-1.5 whitespace-nowrap rounded-lg px-3 py-2 text-sm font-medium text-primary-600 transition-colors hover:bg-primary-50 dark:text-primary-400 dark:hover:bg-gray-700"
                  aria-label={`Add sub-team to ${viewing.name}`}
                >
                  <Plus className="h-4 w-4" />
                  Add sub-team
                </button>
              )}
              {canAddExistingTeams(viewing, viewer) && (
                <button
                  type="button"
                  onClick={() => fromView(() => setAttachParent(viewing))}
                  className="inline-flex items-center gap-1.5 whitespace-nowrap rounded-lg px-3 py-2 text-sm font-medium text-primary-600 transition-colors hover:bg-primary-50 dark:text-primary-400 dark:hover:bg-gray-700"
                  aria-label={`Add existing team to ${viewing.name}`}
                >
                  <FolderInput className="h-4 w-4" />
                  Add existing team
                </button>
              )}
              <button
                type="button"
                onClick={() => fromView(() => setEditing(viewing))}
                className="inline-flex items-center gap-1.5 whitespace-nowrap rounded-lg border border-gray-300 px-3 py-2 text-sm font-medium text-gray-700 transition-colors hover:bg-gray-50 dark:border-gray-600 dark:text-gray-200 dark:hover:bg-gray-700"
                aria-label={`Edit ${viewing.parentTeamId ? 'sub-team' : 'team'} ${viewing.name}`}
              >
                <Edit2 className="h-4 w-4" />
                Edit
              </button>
            </div>
          </div>
        )}
      </Modal>

      {/* Create modal */}
      <Modal
        isOpen={showCreate}
        onClose={() => (isSavingCreate ? null : setShowCreate(false))}
        title="Create Team"
        size="xl"
      >
        <div className="h-[70vh]">
          <TeamEditor
            onCreate={handleCreate}
            onCancel={() => setShowCreate(false)}
            isSaving={isSavingCreate}
            knownTags={allTags}
          />
        </div>
      </Modal>

      {/* Add sub-team modal */}
      <Modal
        isOpen={!!subTeamParent}
        onClose={() => (isSavingCreate ? null : setSubTeamParent(null))}
        title={subTeamParent ? `Add sub-team to ${subTeamParent.name}` : 'Add sub-team'}
        size="xl"
      >
        {subTeamParent && (
          <div className="h-[70vh]">
            <TeamEditor
              parent={subTeamParent}
              onCreate={handleCreate}
              onCancel={() => setSubTeamParent(null)}
              isSaving={isSavingCreate}
              knownTags={allTags}
            />
          </div>
        )}
      </Modal>

      {/* Edit modal */}
      <Modal
        isOpen={!!editing}
        onClose={() => (isSavingUpdate ? null : setEditing(null))}
        title="Edit Team"
        size="xl"
      >
        {editing && (
          <div className="h-[70vh]">
            <TeamEditor
              team={editing}
              parent={parentOf(editing)}
              parentOptions={viewer.isAdmin ? parentOptionsFor(teams, editing) : undefined}
              onUpdate={handleUpdate}
              onCancel={() => setEditing(null)}
              isSaving={isSavingUpdate}
              knownTags={allTags}
            />
          </div>
        )}
      </Modal>

      {/* Add existing team */}
      <Modal
        isOpen={!!attachParent}
        onClose={() => (updateMutation.isPending ? null : setAttachParent(null))}
        title={attachParent ? `Add existing team to ${attachParent.name}` : 'Add existing team'}
        size="md"
      >
        {attachParent && (
          <AttachExistingTeamDialog
            parent={attachParent}
            teams={teams}
            viewer={viewer}
            onAttach={handleAttach}
            onCancel={() => setAttachParent(null)}
            isSaving={updateMutation.isPending}
          />
        )}
      </Modal>

      {/* Confirm an edit that moves the team to another parent */}
      <Modal
        isOpen={!!pendingMove && !!editing}
        onClose={() => (isSavingUpdate ? null : setPendingMove(null))}
        title="Move team"
        size="md"
      >
        {pendingMove && editing && (
          <MoveTeamConfirmation
            teamId={editing.id}
            teamName={editing.name}
            target={
              pendingMove.dto.parentTeamId
                ? {
                    id: pendingMove.dto.parentTeamId,
                    name: teams.find((t) => t.id === pendingMove.dto.parentTeamId)?.name ?? 'the parent',
                  }
                : null
            }
            onConfirm={(moveParentAgents) =>
              saveUpdate(
                { ...pendingMove.dto, ...(moveParentAgents ? { moveParentAgents: true } : {}) },
                pendingMove.agentIds,
              )
            }
            onCancel={() => setPendingMove(null)}
            isSaving={isSavingUpdate}
          />
        )}
      </Modal>

      {/* Delete confirm */}
      <Modal
        isOpen={!!deleting}
        onClose={() => (isDeleting ? null : closeDelete())}
        title={deleteError ? "Can't delete team" : 'Delete team?'}
        size="md"
      >
        {deleting && (
          <div className="space-y-4">
            {deleteError ? (
              // Blocker popup: team is still referenced by a pipeline routing rule.
              <div className="flex items-start gap-3 rounded-lg border border-red-200 bg-red-50 p-4 dark:border-red-900/50 dark:bg-red-900/20">
                <AlertCircle className="mt-0.5 h-5 w-5 flex-shrink-0 text-red-500" />
                <div className="text-sm">
                  <p className="font-medium text-red-800 dark:text-red-300">
                    Can't delete <strong>{deleting.name}</strong>
                  </p>
                  <p className="mt-1 text-red-700 dark:text-red-400">{deleteError}</p>
                  {/pipeline/i.test(deleteError) && (
                    <p className="mt-2 text-red-700/80 dark:text-red-400/80">
                      Remove this team from the pipeline's <strong>Routing</strong> rules
                      (Channel / Ticket Type / Default Team), then try again.
                    </p>
                  )}
                </div>
              </div>
            ) : (
              <p className="text-sm text-gray-700 dark:text-gray-300">
                Delete <strong>{deleting.name}</strong>? Agents will not be removed from your
                workspace, but they will no longer belong to this team. Any pipeline routing rules
                referencing this team must be reassigned first.
              </p>
            )}
            <div className="flex justify-end gap-3">
              <Button
                variant="secondary"
                onClick={closeDelete}
                disabled={isDeleting}
              >
                {deleteError ? 'Close' : 'Cancel'}
              </Button>
              {!deleteError && (
                <Button
                  variant="danger"
                  onClick={handleDelete}
                  disabled={isDeleting}
                >
                  {isDeleting ? <Spinner size="sm" /> : 'Delete Team'}
                </Button>
              )}
            </div>
          </div>
        )}
      </Modal>
    </div>
  );
}

export default TeamsSettings;
